import type { PhoneNumber } from 'lib-shared-legacy-types';

import { ajaxSend } from 'api/api';

import type {
  BuyGroupQuery,
  BuyGroupResponse,
  BuyNumberQuery,
  BuyNumberResponse,
  PhoneNumbersGetItemsResponse,
  PhoneNumbersQueryParams,
  PhoneNumbersReleaseResponse,
  PhoneNumbersSearchQuery,
  PhoneNumbersSearchResponse,
  UpdateNumberQuery,
  UpdateNumberResponse,
} from './types';

const BASE_PATH = '/v1/numbers';
const AVAILABLE_NUMBERS_PATH = '/v1/available-numbers';

export class TelephonyAPI {
  public getPhoneNumbers(
    query: PhoneNumbersQueryParams,
  ): Promise<PhoneNumbersGetItemsResponse> {
    const isPathParameterPassed = !!query.path;
    const params = new URLSearchParams();

    if (!isPathParameterPassed) {
      const { limit, count, after, before, search } = query;

      if (limit) {
        params.set('limit', limit);
      }

      if (count) {
        params.set('count', count);
      }

      if (after) {
        params.set('after', encodeURIComponent(after));
      }

      if (before) {
        params.set('before', encodeURIComponent(before));
      }

      if (search) {
        params.set('search', search);
      }
    }

    return ajaxSend<PhoneNumbersGetItemsResponse>({
      path: query.path ?? `${BASE_PATH}?${params.toString()}`,
    });
  }

  public searchNumbers(
    query: PhoneNumbersSearchQuery,
  ): Promise<PhoneNumbersSearchResponse> {
    const { country, type, next_page, prev_page } = query;
    const countryCode = country?.country_code?.toUpperCase();
    const params = new URLSearchParams();

    if (type) {
      params.set('numbers_type', type);
    }

    return ajaxSend<PhoneNumbersSearchResponse>({
      path:
        next_page ??
        prev_page ??
        `${AVAILABLE_NUMBERS_PATH}/${countryCode}?${params.toString()}`,
    });
  }

  public releaseNumber(
    number: PhoneNumber,
  ): Promise<PhoneNumbersReleaseResponse> {
    const { e164_format } = number;

    return ajaxSend<PhoneNumbersReleaseResponse>({
      path: `${BASE_PATH}/${e164_format}`,
      method: 'DELETE',
    });
  }

  public updateNumber(query: UpdateNumberQuery): Promise<UpdateNumberResponse> {
    const { number, name, record_outbound_calls } = query;

    return ajaxSend<UpdateNumberResponse>({
      path: `${BASE_PATH}/${number}`,
      method: 'PUT',
      data: {
        name,
        record_outbound_calls,
      },
    });
  }

  public buyNumber(query: BuyNumberQuery): Promise<BuyNumberResponse> {
    const { number, name } = query;
    const {
      phone_number: { e164_format },
    } = number;

    return ajaxSend<BuyNumberResponse>({
      path: BASE_PATH,
      method: 'POST',
      data: {
        phone_number: {
          e164_format,
        },
        name,
      },
    });
  }

  public buyGroup(query: BuyGroupQuery): Promise<BuyGroupResponse> {
    const { group, name } = query;
    const { group_id } = group;

    return ajaxSend<BuyGroupResponse>({
      path: BASE_PATH,
      method: 'POST',
      data: {
        group: {
          group_id,
        },
        name,
      },
    });
  }
}

export default new TelephonyAPI();
